import React from "react";
import { motion } from "framer-motion";
import {
    FileText,
    Trophy,
    FileCheck,
    UserCheck,
    ShieldCheck,
    BookOpenCheck,
} from "lucide-react";

const features = [
    {
        id: 1,
        icon: <FileText size={22} />,
        title: "Model Answers",
        description:
            "Access high-quality sample answers reviewed by UPSC toppers and mentors.",
    },
    {
        id: 2,
        icon: <Trophy size={22} />,
        title: "Rank List",
        description:
            "See how you stand among your peers with a transparent weekly leaderboard.",
    },
    {
        id: 3,
        icon: <FileCheck size={22} />,
        title: "PDF Evaluation",
        description:
            "Get your answers evaluated with detailed remarks and downloadable PDFs.",
    },
    {
        id: 4,
        icon: <UserCheck size={22} />,
        title: "Mentor Feedback",
        description:
            "Personalized reviews by subject matter experts to guide your improvement.",
    },
    {
        id: 5,
        icon: <ShieldCheck size={22} />,
        title: "Integrity Checked",
        description:
            "Strict plagiarism checks to ensure authenticity and originality.",
    },
    {
        id: 6,
        icon: <BookOpenCheck size={22} />,
        title: "Topic Tags",
        description:
            "Each question is tagged to the syllabus and PYQs for better alignment.",
    },
];

const FeatureSection = () => {
    // Sharp, professional animation variants matching the established theme
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.05 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="relative py-24 px-4 sm:px-6 lg:px-8 bg-white w-full overflow-hidden border-b border-gray-100">
            {/* Structural Background Accent (Fixed Dot Grid rendering) */}
            <div className="absolute inset-0 w-full h-full opacity-[0.03] pointer-events-none z-0">
                <div className="w-full h-full bg-repeat bg-[length:30px_30px] bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMSIgY3k9IjEiIHI9IjEiIGZpbGw9IiMwMDAiLz48L3N2Zz4=')]"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Standardized Editorial Header */}
                <div className="mb-14 text-left max-w-2xl">
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                            <span className="w-6 h-1 bg-red-700"></span>
                            Core Capabilities
                        </div>
                        <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-4">
                            Platform{" "}
                            <span className="text-yellow-500">Features.</span>
                        </h2>
                        <p className="text-gray-500 text-lg font-medium leading-relaxed">
                            Everything you need to elevate your mains answer
                            writing preparation with clinical precision.
                        </p>
                    </motion.div>
                </div>

                {/* Feature Modular Grid */}
                <motion.div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-gray-100 bg-white"
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    {features.map((feature) => (
                        <motion.div
                            key={feature.id}
                            variants={itemVariants}
                            className="group relative bg-white p-8 lg:p-10 border-r border-b border-gray-100 hover:bg-slate-50/50 transition-all duration-300 overflow-hidden"
                        >
                            {/* Subtle Hover Accent */}
                            <div className="absolute top-0 left-0 w-[3px] h-0 bg-yellow-400 group-hover:h-full transition-all duration-500 ease-out"></div>

                            {/* Icon Logic */}
                            <div className="mb-8 relative inline-block">
                                <div className="w-12 h-12 bg-yellow-400 text-neutral-900 rounded-lg flex items-center justify-center transition-all duration-300 relative z-10 shadow-sm">
                                    {feature.icon}
                                </div>
                                {/* Secondary decorative shape */}
                                <div className="absolute -top-1.5 -left-1.5 w-12 h-12 border border-yellow-500/30 rounded-lg z-0 group-hover:translate-x-1 group-hover:translate-y-1 transition-transform duration-300"></div>
                            </div>

                            {/* Text Content */}
                            <h3 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-red-700 transition-colors duration-300">
                                {feature.title}
                            </h3>
                            <p className="text-gray-500 text-sm leading-relaxed font-medium">
                                {feature.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default FeatureSection;
